import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import _bind from 'lodash-es/bind';
import _partial from 'lodash-es/partial';
import { Store } from '@shared/core/utils';

export enum LoadingStates {
  init = 'init',
  loading = 'loading',
  success = 'success',
  error = 'error',
}

type LoadingProgressMap = Map<string, LoadingStates>;

const initialState = {
  loaders: new Map<string, LoadingStates>(),
};

class LoadingProgress {
  constructor(private setProgressFn: (loadingState: LoadingStates) => void) {}

  loading(): void {
    this.setProgressFn(LoadingStates.loading);
  }

  success(): void {
    this.setProgressFn(LoadingStates.success);
  }

  error(): void {
    this.setProgressFn(LoadingStates.error);
  }
}

/**
 * @deprecated
 */
export const loadingProgressDefaultSuccessError =
  (progress: LoadingProgress) =>
  <T>(source: Observable<T>) =>
    source.pipe(
      tap(() => progress.success()),
      catchError(error => {
        progress.error();
        return throwError(error);
      }),
    );

/**
 * @deprecated
 */
@Injectable({
  providedIn: 'root',
})
export class LoadingProgressService {
  private store = new Store<typeof initialState>(initialState);

  getProgress(progressName: string): Observable<LoadingStates> {
    return this.store
      .asReadonly()
      .select('loaders')
      .pipe(
        map((loaders: LoadingProgressMap) => {
          if (loaders.has(progressName)) {
            return loaders.get(progressName);
          }

          this.setProgress(progressName, LoadingStates.init);

          return LoadingStates.init;
        }),
      );
  }

  progress(progressName: string): LoadingProgress {
    this.setProgress(progressName, LoadingStates.init);

    return new LoadingProgress(_partial(_bind(this.setProgress, this), progressName));
  }

  private setProgress(progressName: string, loadingState: LoadingStates): void {
    this.store.update(state => {
      const loaders = new Map(state.loaders);
      loaders.set(progressName, loadingState);

      return {
        ...state,
        loaders,
      };
    });
  }
}
